import { gql } from '@apollo/client';

/**
 * Career Center — LMS student surface (spec 0018, increment A).
 *
 * Operation names are deliberately distinct from the backend field names
 * (`MyCareerCenterInfo` vs `myCareerCenter`, `CareerLessonsList` vs
 * `careerLessons`, `CareerLessonDetail` vs `careerLesson`) — equal names make
 * codegen collapse the generated result type into the schema type
 * (graphql-codegen skill).
 */

/**
 * Membership + overall progress. Returns `null` when the student is NOT
 * enrolled — that null IS the membership check (spec 0018 §3.1), not an error.
 * Everything in the section (menu entry, route, lesson list) gates on it.
 */
export const MyCareerCenterInfoQuery = gql`
  query MyCareerCenterInfo {
    myCareerCenter {
      memberId
      status
      directionTitle
      enrolledAt
      lessonsCompleted
      lessonsTotal
    }
  }
`;

/**
 * Published lessons with per-viewer lock state; `[]` for non-members.
 * Deliberately does NOT select `content` / `videoUrl` / `attachments`: the list
 * only needs the teaser, and for a locked lesson the server returns those as
 * null/[] anyway (spec 0018 §4 — the server is the gate, not the client).
 *
 * `isOpened` / `isBookmarked` (spec 0020 Фаза B/C, per-viewer engagement) drive
 * the row markers — a «просмотрен» eye for an opened-but-not-completed lesson and
 * a ★ for a bookmarked one. They never affect the unlock chain.
 */
export const CareerLessonsListQuery = gql`
  query CareerLessonsList {
    careerLessons {
      id
      order
      title
      summary
      durationMin
      completionKind
      isUnlocked
      isCompleted
      completedAt
      isOpened
      isBookmarked
    }
  }
`;

/**
 * A single lesson including the gated payload. When `isUnlocked === false` the
 * server returns `content: null`, `videoUrl: null`, `attachments: []` — the UI
 * renders the lock from `isUnlocked` alone and never guesses at unlock rules.
 *
 * Spec 0020 Фаза B/C per-viewer engagement:
 *  - `isOpened` / `openedAt` / `openCount` — «просмотрен» state (distinct from
 *    completed; opening never unlocks the next lesson).
 *  - `myNotes` — the student's PRIVATE notes; server returns them only on the
 *    LMS annotation, so staff never see them (spec 0020 §3.3).
 *  - `isBookmarked` — the ☆/★ toggle.
 *  - `quizQuestions.correctIndexes` is ALWAYS `[]` here — the answer key stays
 *    server-side and is only revealed in the `submitCareerQuiz` result.
 */
export const CareerLessonDetailQuery = gql`
  query CareerLessonDetail($id: ID!) {
    careerLesson(id: $id) {
      id
      order
      title
      summary
      durationMin
      completionKind
      artifactHint
      isUnlocked
      isCompleted
      completedAt
      isOpened
      openedAt
      openCount
      myNotes
      isBookmarked
      content
      videoUrl
      attachments {
        id
        originalName
        contentType
        sizeBytes
        url
      }
      quizQuestions {
        id
        order
        text
        options
        correctIndexes
      }
    }
  }
`;

/**
 * The member's vacancy feed (spec 0018, increment B2). Operation name
 * `MyCareerVacancies` is deliberately distinct from the backend field
 * `careerVacancies` — equal names collapse the generated result type
 * (graphql-codegen skill).
 *
 * Returns `[]` for a non-member — the backend gates the same way as
 * `careerLessons`, answering with an empty list rather than an error, and
 * yields only PUBLISHED, unexpired rows.
 *
 * `myInteraction` is the caller's own state ('saved' | 'applied' | null); the
 * feed reads it to render the Save / Apply buttons. There is no LMS directions
 * query, so the direction filter's options are derived from the feed's own
 * `directionIds` / `directionTitles` (see useCareerVacancies).
 */
export const MyCareerVacanciesQuery = gql`
  query MyCareerVacancies(
    $directionId: ID
    $skillId: ID
    $workMode: String
    $seniority: String
    $search: String
    $savedOnly: Boolean
  ) {
    careerVacancies(
      directionId: $directionId
      skillId: $skillId
      workMode: $workMode
      seniority: $seniority
      search: $search
      savedOnly: $savedOnly
    ) {
      id
      title
      companyName
      applyUrl
      location
      workMode
      seniority
      employmentType
      salaryMin
      salaryMax
      salaryCurrency
      description
      publishedAt
      expiresAt
      directionIds
      directionTitles
      skillNames
      myInteraction
    }
  }
`;
